/**
 * Shared fuzzy (subsequence) search helper — BO UI Standard §9 (decision
 * 2026-07-08). A query matches when its characters appear IN ORDER anywhere
 * across the text (case-insensitive), NOT a strict substring — so typing
 * `mbwkly` still finds "Main Biweekly" and `epaph` finds "EPA pH".
 *
 * `fuzzyScore` returns 0 for no match and a positive number otherwise, where a
 * higher score is a better match (a whole-substring hit, a prefix hit, and
 * contiguous / word-boundary runs all rank above scattered characters), so a
 * caller can sort results best-match-first. `fuzzyMatch` is the boolean gate;
 * `fuzzyFilter` filters + ranks a list in one call.
 *
 * This is the ONE canonical fuzzy matcher — never hand-roll a per-form
 * `.toLowerCase().includes(...)` substring filter for a List View search box.
 */

/** Characters that mark a word boundary in the searched text (for the run bonus). */
const WORD_BREAK = /[\s\-_/.,:()]/;

/**
 * Subsequence score. 0 = no match. Higher = better.
 * @param query the user's raw search text (case-insensitive)
 * @param text  the row/item text to test against
 */
export function fuzzyScore(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1; // empty query matches everything (neutral, non-zero)
  if (!text) return 0;
  const t = text.toLowerCase();

  let score = 0;
  let ti = 0; // next index in text we may consume
  let prevMatch = -2; // index of the previous matched char (for contiguity)
  let run = 0; // length of the current contiguous run

  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    for (let j = ti; j < t.length; j++) {
      if (t[j] === ch) {
        found = j;
        break;
      }
    }
    if (found === -1) return 0; // a query char is missing in order → no match

    if (found === prevMatch + 1) {
      run += 1;
      score += 5 + run; // contiguous chars are worth much more than scattered ones
    } else {
      run = 0;
      score += 1;
    }
    // bonus when the matched char starts a word (index 0 or after a break char)
    const prevChar = found > 0 ? t.charAt(found - 1) : '';
    if (found === 0 || WORD_BREAK.test(prevChar)) score += 3;

    prevMatch = found;
    ti = found + 1;
  }

  // whole-substring is the strongest signal; a prefix substring stronger still
  const subIdx = t.indexOf(q);
  if (subIdx !== -1) score += 10 + (subIdx === 0 ? 5 : 0);

  return score;
}

/** Boolean gate — true when `query` fuzzy-matches `text` (empty query = true). */
export function fuzzyMatch(query: string, text: string): boolean {
  return fuzzyScore(query, text) > 0;
}

/**
 * Filter + rank a list by fuzzy match against each item's searchable text.
 * Returns the original list untouched when the query is blank; otherwise only
 * matching items, best match first. Stable within equal scores (original order).
 */
export function fuzzyFilter<T>(items: readonly T[], query: string, textOf: (item: T) => string): T[] {
  if (!query.trim()) return items.slice();
  return items
    .map((item, i) => ({ item, i, score: fuzzyScore(query, textOf(item)) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.item);
}
